import _sortBy from 'lodash/sortBy';
import _get from 'lodash/get';

export default class ProductCheckTable {
  /* @ngInject */
  constructor(ProductService, $translate) {
    this.productService = ProductService;
    this._translate = $translate;
    this.prices = {};
    this.employeePrices = {};
    this.isLoadingPrices = {};
    this.isLoadingEmployeePrices = {};
    this.showEmployeePriceHeader = true;
  }

  $onChanges({stockList, ecommerceStockList, stores}) {
    const _stores = _get(stores, 'currentValue');
    const _stockList = _get(stockList, 'currentValue');
    const _ecommerceStockList = _get(ecommerceStockList, 'currentValue');
    if (_stores) {
      this.stores = _stores || [];
    }
    if (_stockList) {
      this.activeProductBarcode = null;
      this.activeStoreId = null;
      this.tableTitles = this._getStockTableTitle(_ecommerceStockList);
      this.tableProducts = _sortBy(this._formatDataTable(_stockList, _ecommerceStockList), 'size');
    }
  }

  toggleProduct(product) {
    this.activeStoreId = null;
    if (this.isActiveProduct(product)) {
      return this.activeProductBarcode = null;
    }
    this.activeProductBarcode = product.barcode;
  }

  isActiveProduct(product) {
    return this.activeProductBarcode === product.barcode;
  }

  toggleStore(store = {}) {
    if (this.isActiveStore(store)) {
      return this.activeStoreId = null;
    }
    this.activeStoreId = store.storeId;

  }

  isActiveStore(store) {
    return this.activeStoreId === store.storeId;
  }

  isGetPriceButtonVisible(storeId, barcode) {
    const priceKey = this.getPriceKey(storeId, barcode);
    return !this.isLoadingPrices[priceKey] && !this.prices[priceKey];
  }

  isGetEmployeePriceButtonVisible(storeId, barcode) {
    const priceKey = this.getPriceKey(storeId, barcode);
    return !this.isLoadingEmployeePrices[priceKey] && !this.employeePrices[priceKey];
  }

  getPrice(storeId, barcode) {
    const priceKey = this.getPriceKey(storeId, barcode);
    this.isLoadingPrices[priceKey] = true;
    this.productService.getBarcodePriceByStoreId(storeId, barcode)
      .then(res => this.prices[priceKey] = res)
      .catch(() => this.prices[priceKey] = {})
      .finally(() => {
        this.isLoadingPrices[priceKey] = false;
        delete this.isLoadingPrices[priceKey];
      });
  }

  getEmployeePrice(storeId, barcode) {
    const priceKey = this.getPriceKey(storeId, barcode);
    this.isLoadingEmployeePrices[priceKey] = true;
    this.productService.getBarcodeEmployeePriceByStoreId(storeId, barcode)
      .then(res => this.employeePrices[priceKey] = res)
      .catch(() => this.employeePrices[priceKey] = {})
      .finally(() => {
        this.isLoadingEmployeePrices[priceKey] = false;
        delete this.isLoadingEmployeePrices[priceKey];
      });
  }

  getPriceKey(storeId, barcode) {
    return storeId + barcode;
  }

  _getStockTableTitle(ecommerceMap) {
    const size = this._translate.instant('PRODUCT.SIZE');
    const stock = this._translate.instant('PRODUCT.STOCK');
    const ecommerce = !!ecommerceMap.ecommerce && this._translate.instant('PRODUCT.ECOMMERCE');
    const omnistock = !!ecommerceMap.omnistock && this._translate.instant('PRODUCT.OMNISTOCK');
    return [size, ecommerce, omnistock, stock].filter(v => !!v);
  }

  _getProduct(barcode, size, map, enrichWith = {}) {
    return map.get(barcode) || {barcode, size, stores: [], ...enrichWith};
  }

  _getStore(storeId, stores, enrichWith = {}) {
    const currentStoreInfo = stores.find(s => s.id === storeId) || {};
    return this._createTableStore(currentStoreInfo, {...enrichWith});
  }

  _createTableStore({id, name, address, phone, isRevolutionised}, {quantity = 0} = {}) {
    const ensureAddress = address || {};
    const labels = [name, quantity];
    const _address = `${ensureAddress.address || ''} ${ensureAddress.city || ''} ${ensureAddress.street1 || ''} ${phone || ''}`.replace(/\s{2,}/g, ' ');
    return {storeId: id, name, address: _address, quantity, labels, isRevolutionised};
  }

  _createEcommerceMap(ecommerceTypeList) {
    if (!ecommerceTypeList) {
      return;
    }
    return ecommerceTypeList.reduce((acc, ecommerceStock) => {
      const oldQuantity = acc[ecommerceStock.size.barcode];
      const newQuantity = ecommerceStock.quantity || 0;
      acc[ecommerceStock.size.barcode] = oldQuantity ? (oldQuantity + newQuantity) : newQuantity;
      return acc;
    }, {});
  }

  _createStockMapByBarcode(stockList, stores, ecommerceStockList = {ecommerce: undefined, omnistock: undefined}) {
    const ecommerceMap = this._createEcommerceMap(ecommerceStockList.ecommerce);
    const omnistockMap = this._createEcommerceMap(ecommerceStockList.omnistock);
    return stockList.reduce((map, stock) => {
      const barcode = stock.size.barcode;
      const enrichmentEcommerce = ecommerceMap ? {ecommerce: ecommerceMap[barcode] || 0} : {};
      const enrichmentOmnistock = omnistockMap ? {omnistock: omnistockMap[barcode] || 0} : {};
      const product = this._getProduct(barcode, stock.size.label, map, {...enrichmentEcommerce, ...enrichmentOmnistock});
      const quantity = stock.quantity;
      const store = this._getStore(stock.storeId, stores, {quantity});
      product.stores.push(store);
      return map.set(barcode, product);
    }, new Map());
  }

  _formatDataTable(stockList, ecommerceStockList) {
    const isStock = !!stockList;
    const dataMap = this._createStockMapByBarcode(stockList, this.stores, ecommerceStockList);
    dataMap.forEach(product => this._enrichProduct(product, isStock));
    return Array.from(dataMap.values());
  }

  _enrichProduct(product) {
    const {label} = this._getStockToShowInTable(product.stores);
    product.labels = [product.size, product.ecommerce, product.omnistock, label].filter(v => v !== undefined && v !== null);
  }

  _getStockToShowInTable(stores) {
    const quantity = this._getTotalQuantityInStores(stores);
    return {value: quantity, label: quantity};
  }

  _getTotalQuantityInStores(stores) {
    return stores.reduce((acc, store) => {
      acc += store.quantity;
      return acc;
    }, 0);
  }
}



// WEBPACK FOOTER //
// ./src/app/product/product-check/product-check-table/product-check-table.controller.js